/**
 * Admin: Property Manager Applications queue (Phase 2L-A111 / Phase 1).
 *
 * Lives at /admin/partners/applications. Linked from a tab strip on
 * /admin/partners so admins can flip between "All partners" and the
 * pending applications queue.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, CheckCircle2, X, Building2, ArrowLeft, Mail, Phone, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";

const STATUSES = ["pending", "approved", "declined"];

const BADGE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  declined: "bg-rose-100 text-rose-700",
};

export default function AdminPropertyManagerApplications() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/property-manager-applications", { params: { status } })
         .then(({ data }) => setItems(data || [])),
      api.get("/admin/property-manager-applications/metrics")
         .then(({ data }) => setMetrics(data)),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <PortalShell>
      <div data-testid="admin-pm-applications-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/admin/partners" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-cyan-700 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Partners
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">Property Manager Onboarding</div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">Applications queue</h1>
            <p className="text-slate-500 mt-1 text-sm">
              Review, approve or decline incoming Property Manager applications.
              Approval auto-creates the partner + login + welcome email.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <TestNotificationButton />
          </div>
        </div>

        {/* Status tabs */}
        <div className="mt-7 inline-flex rounded-full bg-slate-100 p-1 text-sm">
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`pm-app-status-tab-${s}`}
              onClick={() => setStatus(s)}
              className={`px-4 py-1.5 rounded-full font-semibold capitalize transition ${
                status === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {s} {metrics ? `· ${metrics[s] || 0}` : ""}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="mt-6 grid lg:grid-cols-[1fr,420px] gap-6">
          <div data-testid="pm-app-list" className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {loading && <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
            {!loading && items.length === 0 && (
              <div data-testid="pm-app-empty" className="p-10 text-center text-slate-400">
                <Inbox className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                No {status} applications.
              </div>
            )}
            {!loading && items.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2">Business</th>
                    <th className="text-left px-4 py-2">Contact</th>
                    <th className="text-left px-4 py-2">Submitted</th>
                    <th className="text-left px-4 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((a) => (
                    <tr
                      key={a.id}
                      data-testid={`pm-app-row-${a.id}`}
                      onClick={() => setSelected(a)}
                      className={`cursor-pointer border-t border-slate-100 hover:bg-cyan-50/40 ${
                        selected?.id === a.id ? "bg-cyan-50/60" : ""
                      }`}
                    >
                      <td className="px-4 py-3 font-semibold text-slate-900">{a.business_name}</td>
                      <td className="px-4 py-3 text-slate-700">{a.contact_name} <div className="text-[11px] text-slate-400">{a.email}</div></td>
                      <td className="px-4 py-3 text-slate-500">{formatDateAU(a.submitted_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${BADGE[a.status] || "bg-slate-100"}`}>{a.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Detail */}
          {selected
            ? <ApplicationDetail app={selected} onClose={() => setSelected(null)} onChanged={() => { load(); setSelected(null); }} />
            : <div className="bg-white rounded-2xl border border-slate-200 p-8 text-sm text-slate-400 text-center self-start">
                Select an application to view details.
              </div>}
        </div>
      </div>
    </PortalShell>
  );
}

function ApplicationDetail({ app, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [adminNotes, setAdminNotes] = useState(app.admin_notes || "");
  const [declineReason, setDeclineReason] = useState("");
  const [sendDeclineEmail, setSendDeclineEmail] = useState(false);

  const saveNotes = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/property-manager-applications/${app.id}/notes`, { admin_notes: adminNotes });
      toast.success("Internal notes saved");
    } catch (e) {
      toast.error("Could not save notes");
    } finally { setBusy(false); }
  };

  const approve = async () => {
    if (!confirm(`Approve ${app.business_name}? This creates a partner + login and emails a setup link.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/property-manager-applications/${app.id}/approve`);
      if (data.welcome_email_status === "sent") {
        toast.success(`Approved · welcome email sent to ${app.email}`);
      } else {
        toast.warning(`Approved, but welcome email ${data.welcome_email_status}. Resend the setup link manually.`);
      }
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/property-manager-applications/${app.id}/decline`,
                     { reason: declineReason, send_email: sendDeclineEmail });
      toast.success(`Declined${sendDeclineEmail ? " · email sent" : ""}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decline failed");
    } finally { setBusy(false); }
  };

  // Phase 2L-A133 — P0 recovery action. Re-sends the welcome email
  // (with a FRESH password-reset token) for an already-approved
  // application. Use cases: applicant deleted the original email,
  // Resend rate-limit caused the first send to fail, or the original
  // token expired before they clicked it.
  const resendWelcome = async () => {
    if (!confirm(`Resend the welcome email to ${app.email}? A new password setup link will be issued.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/property-manager-applications/${app.id}/resend-welcome`,
      );
      if (data.welcome_email_status === "sent") {
        toast.success(`Welcome email re-sent to ${data.to}`);
      } else {
        toast.error(
          `Resend ${data.welcome_email_status}: ${data.welcome_email_error || "unknown error"}`,
        );
      }
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="pm-app-detail" className="bg-white rounded-2xl border border-slate-200 p-6 self-start">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-cyan-600">
            <Building2 className="w-3 h-3" /> {app.status}
          </div>
          <h3 data-testid="pm-app-detail-business" className="font-display font-bold text-slate-900 text-lg mt-1">{app.business_name}</h3>
          <div className="mt-1 text-sm text-slate-600">{app.contact_name}</div>
        </div>
        <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
      </div>

      <div className="mt-4 space-y-2 text-sm">
        <Field label="Email"><a href={`mailto:${app.email}`} className="text-cyan-700">{app.email}</a></Field>
        <Field label="Phone"><a href={`tel:${app.phone}`} className="text-slate-700">{app.phone}</a></Field>
        <Field label="Office address">{app.office_address || "—"}</Field>
        <Field label="Areas managed">{(app.areas_managed || []).join(", ") || "—"}</Field>
        <Field label="Approx. rural properties">{app.approx_rural_property_count ?? "—"}</Field>
        <Field label="Notes">{app.notes || "—"}</Field>
        <Field label="Submitted">{formatDateAU(app.submitted_at)}</Field>
      </div>

      <div className="mt-5">
        <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Internal admin notes</label>
        <textarea
          data-testid="pm-app-admin-notes"
          rows={3}
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
        <button onClick={saveNotes} disabled={busy} data-testid="pm-app-save-notes"
                className="mt-1.5 text-xs text-cyan-700 font-semibold hover:underline disabled:opacity-50">
          Save notes
        </button>
      </div>

      {app.status === "pending" && (
        <div className="mt-5 pt-5 border-t border-slate-100 space-y-4">
          <button
            disabled={busy} onClick={approve} data-testid="pm-app-approve-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-5 py-2.5 text-sm font-semibold transition"
          >
            <CheckCircle2 className="w-4 h-4" /> Approve · create partner & send welcome
          </button>

          <div className="rounded-xl bg-rose-50/40 border border-rose-100 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Decline
            </div>
            <textarea
              data-testid="pm-app-decline-reason"
              rows={2}
              placeholder="Optional reason (private unless you choose to email)"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="w-full rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm"
            />
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox" checked={sendDeclineEmail} data-testid="pm-app-send-decline-email"
                onChange={(e) => setSendDeclineEmail(e.target.checked)}
              />
              Also email the applicant a polite decline notice
            </label>
            <button
              disabled={busy} onClick={decline} data-testid="pm-app-decline-btn"
              className="w-full inline-flex items-center justify-center rounded-full border border-rose-300 bg-white hover:bg-rose-50 text-rose-700 px-5 py-2 text-sm font-semibold transition disabled:opacity-50"
            >
              Decline application
            </button>
          </div>
        </div>
      )}

      {app.status === "approved" && (
        <div className="mt-5 pt-5 border-t border-slate-100 text-sm space-y-3">
          <Field label="Approved at">{formatDateAU(app.decided_at)}</Field>
          <Field label="Partner ID">{app.approved_partner_id}</Field>
          <Field label="User ID">{app.approved_user_id}</Field>
          <button
            disabled={busy} onClick={resendWelcome}
            data-testid="pm-app-resend-welcome-btn"
            className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full border border-cyan-300 bg-white hover:bg-cyan-50 text-cyan-700 px-5 py-2 text-sm font-semibold transition disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4" /> Resend approval email
          </button>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Issues a fresh password setup link and emails it to{" "}
            <span className="font-mono">{app.email}</span>. Use this if
            the applicant deleted the original email or the link expired.
          </p>
        </div>
      )}

      {app.status === "declined" && (
        <div className="mt-5 pt-5 border-t border-slate-100 text-sm">
          <Field label="Declined at">{formatDateAU(app.decided_at)}</Field>
          <Field label="Reason">{app.decline_reason || "—"}</Field>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-slate-400 text-[11px] uppercase tracking-wider font-bold whitespace-nowrap pt-0.5">{label}</span>
      <span className="text-slate-700 text-right">{children}</span>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// Phase 2L-A131 — Test Notification button
//
// One-click "send a sample admin notification" so operators can
// instantly verify the inbox is wired correctly. Fires
// POST /api/admin/property-manager-applications/test-notification,
// which renders the same HTML template the live submit flow uses but
// flags the sample doc as a TEST so it's never confused with a real
// application.
// ════════════════════════════════════════════════════════════════════
function TestNotificationButton() {
  const [sending, setSending] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState("");

  const fire = async () => {
    setSending(true);
    try {
      const body = {};
      const v = (overrideEmail || "").trim();
      if (v) body.recipient = v;
      const { data } = await api.post(
        "/admin/property-manager-applications/test-notification",
        body,
      );
      toast.success(
        `Test email sent to ${data.recipient}. ` +
        "Check that inbox (including spam) within ~1 minute.",
        { duration: 9000 },
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e.message ||
        "Failed to send test notification.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1.5">
      <button
        type="button"
        data-testid="pm-apps-send-test-notification"
        onClick={fire}
        disabled={sending}
        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider px-3.5 py-2 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-300 hover:bg-cyan-600 hover:text-white hover:border-cyan-700 disabled:opacity-50 transition-colors"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
        {sending ? "Sending…" : "Send me a test notification"}
      </button>
      <button
        type="button"
        data-testid="pm-apps-test-notification-override-toggle"
        onClick={() => setShowOverride((s) => !s)}
        className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 hover:text-slate-700"
      >
        {showOverride ? "Hide override" : "Send to a specific inbox…"}
      </button>
      {showOverride && (
        <input
          type="email"
          data-testid="pm-apps-test-notification-override-input"
          value={overrideEmail}
          onChange={(e) => setOverrideEmail(e.target.value)}
          placeholder="noosa@pristinewater.net.au"
          className="text-xs font-mono px-2.5 py-1.5 rounded border border-slate-300 w-72"
        />
      )}
    </div>
  );
}

